/**
 * Global Application State for TitCock.tv
 *
 * This module manages all shared state across the application.
 * Import state objects and modify them directly, or use the
 * provided helper functions.
 */

// ==========================================================================
// Configuration Constants
// ==========================================================================

export const CONFIG = {
  DIRECT_VIDEO_CACHES: [
    { path: "boobs.json", category: "boobs", label: "Boobs" },
    { path: "blowjob.json", category: "blowjob", label: "Blowjob" },
    { path: "ass.json", category: "ass", label: "Ass" },
  ],
  RATE_LIMIT: 8,
  MAX_CACHED_VIDEOS: 50,
  PREFETCH_THRESHOLD: 7,
  INITIAL_POST_LIMIT: 40,
  SUBSEQUENT_POST_LIMIT: 60,
  MAX_SYNC_CARDS: 10,
  VAST_URL: "https://s.magsrv.com/v1/vast.php?idzone=5991078",
  VAST_TIMEOUT: 10000,
  VAST_AD_INTERVAL: 8,
};

// ==========================================================================
// DOM Element References
// ==========================================================================

export const elements = {
  // Populated by app.js on init
  gate: null,
  scroller: null,
  content: null,
  headerTitle: null,
  headerAuthor: null,
  categoryFilter: null,
  headerCategory: null,
  categoryMenu: null,
  header: null,
  globalControls: null,
  muteBtn: null,
  muteIcon: null,
  shareBtn: null,
  shareIcon: null,
  likeBtn: null,
  likeIcon: null,
  likeCount: null,
  notifier: null,
  fsBtn: null,
  fsIcon: null,
  savedIcon: null,
  settingsIcon: null,
  homeBtn: null,
  homeIcon: null,
  savedBtn: null,
  settingsBtn: null,
  viewsSlider: null,
  galleryGrid: null,
  galleryContainer: null,
  savedEmpty: null,
  savedViewer: null,
  savedViewerBack: null,
  savedViewerContent: null,
  footer: null,
  footerIndicator: null,
  settingsHome: null,
  legalBtn: null,
  legalView: null,
  legalBackBtn: null,
};

// ==========================================================================
// Feed State
// ==========================================================================

export const feedState = {
  isLoading: false,
  initialFeedLoaded: false,
  seenSlugs: new Set(),
  cacheIndex: 0,
  selectedCategories: new Set(
    CONFIG.DIRECT_VIDEO_CACHES.map((cache) => cache.category),
  ),
};

// ==========================================================================
// Player State
// ==========================================================================

export const playerState = {
  currentIndex: 0,
  activeIndex: -1,
  isAnimating: false,
  globalMuted: true,
  firstCardReady: false,
  videoCache: [],
  blobCache: new Map(),

  animationId: null,
  gesture: {
    active: false,
    directionLocked: false,
    startX: 0,
    startY: 0,
    lastY: 0,
    velocity: 0,
    lastTime: 0,
  },
  doubleTap: {
    lastTime: 0,
    lastIndex: -1,
  },
  wheelBusy: false,
};

// ==========================================================================
// Views State
// ==========================================================================

export const viewsState = {
  currentView: "home", // "home" | "saved" | "settings"
  isSwitching: false,

  animationId: null,
  swipe: {
    active: false,
    didSwipe: false,
    directionLocked: false,
    isHorizontal: false,
    startX: 0,
    startY: 0,
    currentX: 0,
    lastX: 0,
    velocity: 0,
    lastTime: 0,
    startView: "home",
  },
};

// ==========================================================================
// Saved Videos State
// ==========================================================================

export const savedState = {
  isViewerActive: false,
  cards: [],
  currentIndex: 0,
  activeIndex: -1,
  isAnimating: false,

  animationId: null,
  gesture: {
    active: false,
    directionLocked: false,
    startX: 0,
    startY: 0,
    lastY: 0,
    velocity: 0,
    lastTime: 0,
  },
  doubleTap: {
    lastTime: 0,
    lastIndex: -1,
  },
  wheelBusy: false,
};

// ==========================================================================
// Likes State
// ==========================================================================

export const likesState = {
  slugs: new Set(),
  videosData: new Map(),
};

// ==========================================================================
// Notification State
// ==========================================================================

export const notifyState = {
  timeout: null,
  currentNode: null,
};

// ==========================================================================
// Helper Functions
// ==========================================================================

/**
 * Get all card elements in the content area
 * @returns {HTMLElement[]}
 */
export const getCards = () => [
  ...(elements.content?.querySelectorAll(".card") || []),
];

/**
 * Get the currently active card element
 * Uses currentIndex during gestures/animations for immediate button response
 * @returns {HTMLElement|null}
 */
export const getCurrentCard = () => {
  if (savedState.isViewerActive) {
    return (
      savedState.cards[savedState.activeIndex] ||
      savedState.cards[savedState.currentIndex] ||
      null
    );
  }

  // During gestures or animations, use currentIndex for immediate response
  if (playerState.gesture.active || playerState.isAnimating) {
    return getCards()[playerState.currentIndex] || null;
  }

  return getCards()[playerState.activeIndex] || null;
};

/**
 * Get the video element from a card
 * @param {HTMLElement} card
 * @returns {HTMLVideoElement|null}
 */
export const getVideo = (card) => card?.querySelector("video") || null;

/**
 * Get neighboring cards (prev, current, next)
 * @param {number} index
 * @returns {HTMLElement[]}
 */
export const getNeighborCards = (index) => {
  const cards = getCards();
  return [cards[index - 1], cards[index], cards[index + 1]].filter(Boolean);
};

/**
 * Check if the gate modal is still visible
 * @returns {boolean}
 */
export const isGateVisible = () =>
  !!(elements.gate && elements.gate.isConnected);

/**
 * Reset player state for a fresh start
 */
export const resetPlayerState = () => {
  playerState.currentIndex = 0;
  playerState.activeIndex = -1;
  playerState.isAnimating = false;
  playerState.gesture.active = false;
};

/**
 * Reset saved viewer state
 */
export const resetSavedState = () => {
  savedState.isViewerActive = false;
  savedState.cards = [];
  savedState.currentIndex = 0;
  savedState.activeIndex = -1;
  savedState.isAnimating = false;
  savedState.gesture.active = false;
};
